// DisputesList.js
import { useState, useEffect } from "react";
import {
  getDisputesList,
  createDispute,
  resolveDispute,
  exportDisputesCsv,
} from "../../service/Mainservice";
import ExportCsvButton from "../ExportCsvButton";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import "./Disputes.css";

// MUI Icons
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const DisputesList = () => {
  const [loading, setLoading] = useState(true);
  const [disputes, setDisputes] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [resolveModal, setResolveModal] = useState({
    open: false,
    disputeId: null,
  });
  const [formData, setFormData] = useState({
    orderId: "",
    userId: "",
    userType: "customer",
    disputeType: "missing_items",
    description: "",
  });
  const [resolutionDetails, setResolutionDetails] = useState("");

  const fetchDisputes = async () => {
    setLoading(true);
    try {
      const res = await getDisputesList();
      setDisputes(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, []);

  const handleCreate = async () => {
    if (!formData.orderId || !formData.userId || !formData.description) {
      alert("Please fill all required fields.");
      return;
    }
    try {
      await createDispute({
        orderId: parseInt(formData.orderId),
        userId: parseInt(formData.userId),
        userType: formData.userType,
        disputeType: formData.disputeType,
        description: formData.description,
      });
      setCreateModal(false);
      setFormData({
        orderId: "",
        userId: "",
        userType: "customer",
        disputeType: "missing_items",
        description: "",
      });
      fetchDisputes();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create dispute.");
    }
  };

  const handleResolve = async () => {
    try {
      await resolveDispute({
        disputeId: resolveModal.disputeId,
        resolutionDetails,
      });
      setResolveModal({ open: false, disputeId: null });
      setResolutionDetails("");
      fetchDisputes();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to resolve.");
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      open: "status-pending",
      resolved: "status-completed",
      closed: "status-failed",
    };
    return map[status] || "";
  };

  return (
    <div className="disputes-page">
      <ExportCsvButton exportRequest={exportDisputesCsv} filename="disputes.csv" />
      <div className="page-actions">
        <button className="btn-primary" onClick={() => setCreateModal(true)}>
          <AddIcon /> New Dispute
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="table-wrapper">
          <table className="data_table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Order</th>
                <th>User</th>
                <th>Type</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {disputes.length === 0 ? (
                <tr>
                  <td colSpan="7">No disputes.</td>
                </tr>
              ) : (
                disputes.map((d) => (
                  <tr key={d.id}>
                    <td>{d.id}</td>
                    <td>#{d.orderId}</td>
                    <td>{d.userId}</td>
                    <td>{d.disputeType}</td>
                    <td>{d.description}</td>
                    <td>
                      <span
                        className={`status-badge ${getStatusBadge(d.status)}`}
                      >
                        {d.status}
                      </span>
                    </td>
                    <td>
                      {d.status === "open" && (
                        <button
                          className="btn-primary btn-sm"
                          onClick={() =>
                            setResolveModal({ open: true, disputeId: d.id })
                          }
                        >
                          <CheckCircleIcon fontSize="small" /> Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={createModal}
        onClose={() => setCreateModal(false)}
        title="Log Dispute"
      >
        <div className="form-group">
          <label>Order ID *</label>
          <input
            type="number"
            value={formData.orderId}
            onChange={(e) =>
              setFormData({ ...formData, orderId: e.target.value })
            }
          />
        </div>
        <div className="form-group">
          <label>User ID *</label>
          <input
            type="number"
            value={formData.userId}
            onChange={(e) =>
              setFormData({ ...formData, userId: e.target.value })
            }
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>User Type</label>
            <select
              value={formData.userType}
              onChange={(e) =>
                setFormData({ ...formData, userType: e.target.value })
              }
            >
              <option value="customer">Customer</option>
              <option value="vendor">Vendor</option>
              <option value="delivery">Driver</option>
            </select>
          </div>
          <div className="form-group">
            <label>Dispute Type</label>
            <select
              value={formData.disputeType}
              onChange={(e) =>
                setFormData({ ...formData, disputeType: e.target.value })
              }
            >
              <option value="missing_items">Missing Items</option>
              <option value="wrong_delivery">Wrong Delivery</option>
              <option value="refund_request">Refund Request</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows="3"
          />
        </div>
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={() => setCreateModal(false)}
          >
            Cancel
          </button>
          <button className="btn-primary" onClick={handleCreate}>
            Create
          </button>
        </div>
      </Modal>

      {/* Resolve Modal */}
      <Modal
        isOpen={resolveModal.open}
        onClose={() => setResolveModal({ open: false, disputeId: null })}
        title="Resolve Dispute"
      >
        <div className="form-group">
          <label>Resolution Details *</label>
          <textarea
            value={resolutionDetails}
            onChange={(e) => setResolutionDetails(e.target.value)}
            rows="3"
            placeholder="Describe how the dispute was resolved..."
          />
        </div>
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={() => setResolveModal({ open: false, disputeId: null })}
          >
            Cancel
          </button>
          <button className="btn-primary" onClick={handleResolve}>
            Resolve
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default DisputesList;
